import { Rng, valueNoise2 } from '../core/rng';
import { PARAMS } from './params';

/**
 * Analytic island heightfield — pure math, shared by the sim (worker), the
 * render mesh, tests, and creature placement. Never sampled from GPU.
 *
 * configureTerrain(seed) must be called on BOTH threads with the same world
 * seed before any terrainHeight() call, so the worker and renderer agree on
 * the same island. Each seed grows a differently-shaped coastline with its
 * own ponds, streams, and scattered raised biomes.
 */

export const WATER_LEVEL = -0.45;

export const BIOME = {
  GRASS: 0,
  FLOWER: 1,
  DIRT: 2,
  MOSS: 3,
  SAND: 4,
  MUD: 5,
} as const;
export type Biome = (typeof BIOME)[keyof typeof BIOME];

export interface WaterSpot {
  x: number;
  z: number;
  r: number;
}

const COAST_LUT_N = 512;
const TWO_PI = Math.PI * 2;

let noiseSeed = 1994;
let biomeSeed = 77;
const coastLut = new Float32Array(COAST_LUT_N).fill(PARAMS.ISLAND_RADIUS);
let minCoastR: number = PARAMS.ISLAND_RADIUS;

interface WaterBlob {
  x: number;
  z: number;
  r: number;
  depth: number;
}
let waterBlobs: WaterBlob[] = [];
let pondSpots: WaterSpot[] = [];

// build a default island so tests / early calls never see an empty world
configureTerrain('alpha');

export function configureTerrain(seedString: string): void {
  const rng = Rng.fromString('terrain:' + seedString);
  noiseSeed = (rng.next() * 1e9) | 0;
  biomeSeed = (rng.next() * 1e9) | 0;

  // ---- irregular coastline: harmonic ring + low-freq bays, baked to a LUT ----
  const base = PARAMS.ISLAND_RADIUS;
  const harm: { freq: number; amp: number; phase: number }[] = [];
  for (let k = 0; k < 5; k++) {
    harm.push({
      freq: 2 + k,
      amp: (0.17 / (k * 0.5 + 1)) * rng.range(0.55, 1.35),
      phase: rng.range(0, TWO_PI),
    });
  }
  const ringSeed = (rng.next() * 1e9) | 0;
  minCoastR = base;
  for (let i = 0; i < COAST_LUT_N; i++) {
    const a = (i / COAST_LUT_N) * TWO_PI;
    let f = 1;
    for (const h of harm) f += h.amp * Math.sin(h.freq * a + h.phase);
    // sample noise around a circle so it wraps seamlessly -> bays & capes
    f += (valueNoise2(Math.cos(a) * 2.3 + 40, Math.sin(a) * 2.3 + 40, ringSeed) - 0.5) * 0.5;
    const rad = base * Math.max(0.42, f);
    coastLut[i] = rad;
    if (rad < minCoastR) minCoastR = rad;
  }

  // ---- water features: ponds (big blobs) + streams (chains of small blobs) ----
  waterBlobs = [];
  pondSpots = [];
  const pondCount = 1 + rng.int(3); // 1-3 ponds
  for (let p = 0; p < pondCount; p++) {
    const a = rng.range(0, TWO_PI);
    const rad = rng.range(0.22, 0.58) * minCoastR;
    const px = Math.cos(a) * rad;
    const pz = Math.sin(a) * rad;
    const pr = rng.range(5, 8.5);
    waterBlobs.push({ x: px, z: pz, r: pr, depth: 1.1 });
    pondSpots.push({ x: px, z: pz, r: pr });
    if (rng.next() < 0.75) carveStream(px, pz, rng);
  }
}

/** A wiggling channel of small blobs from a pond out to the sea. */
function carveStream(sx: number, sz: number, rng: Rng): void {
  let x = sx;
  let z = sz;
  let dir = Math.atan2(sz, sx); // head outward toward the coast
  if (sx === 0 && sz === 0) dir = rng.range(0, TWO_PI);
  const steps = 12 + rng.int(10);
  for (let i = 0; i < steps; i++) {
    dir += rng.range(-0.6, 0.6); // meander
    const step = rng.range(1.5, 2.3);
    x += Math.cos(dir) * step;
    z += Math.sin(dir) * step;
    // wider & deeper so streams actually hold water, not just a dry ditch
    waterBlobs.push({ x, z, r: rng.range(2.0, 3.0), depth: 1.05 });
    const r = Math.hypot(x, z);
    if (r > coastRadius(Math.atan2(z, x)) * 0.98) break; // reached the ocean
  }
}

export function coastRadius(angle: number): number {
  let a = angle % TWO_PI;
  if (a < 0) a += TWO_PI;
  const f = (a / TWO_PI) * COAST_LUT_N;
  const i0 = Math.floor(f) % COAST_LUT_N;
  const i1 = (i0 + 1) % COAST_LUT_N;
  const frac = f - Math.floor(f);
  return (coastLut[i0] as number) * (1 - frac) + (coastLut[i1] as number) * frac;
}

/** Soft 0->1 ramp; edges of biome patches slope instead of stepping. */
function band(v: number, lo: number, hi: number): number {
  const t = (v - lo) / (hi - lo);
  return t < 0 ? 0 : t > 1 ? 1 : t * t * (3 - 2 * t);
}

function biomeField(x: number, z: number): number {
  return valueNoise2(x * 0.085 + 12, z * 0.085 + 90, biomeSeed);
}

/** How much a point is under a water blob's influence (1 in/near, 0 far). */
function waterInfluence(x: number, z: number): number {
  let m = 0;
  for (const b of waterBlobs) {
    const dx = x - b.x;
    const dz = z - b.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    const inf = band(b.r * 1.7 - d, 0, b.r * 1.7);
    if (inf > m) m = inf;
  }
  return m;
}

export function terrainHeight(x: number, z: number): number {
  const r = Math.hypot(x, z);
  const angle = Math.atan2(z, x);
  const cr = coastRadius(angle);

  // gentle rolling bumps, lifted so base land stays above the water line
  const n =
    valueNoise2(x * 0.06 + 100, z * 0.06 + 100, noiseSeed) * 0.6 +
    valueNoise2(x * 0.15 + 300, z * 0.15 + 300, noiseSeed) * 0.4;
  const calm = 0.4 + 0.6 * Math.min(1, r / (cr * 0.8));
  let h = 0.25 + (n - 0.5) * 1.2 * calm;

  // carve ponds & streams
  let wInf = 0;
  for (const b of waterBlobs) {
    const dx = x - b.x;
    const dz = z - b.z;
    const d2 = dx * dx + dz * dz;
    if (d2 < b.r * b.r) {
      const t = 1 - Math.sqrt(d2) / b.r; // 0 edge .. 1 center
      h -= b.depth * (t * t * (3 - 2 * t));
    }
    const inf = band(b.r * 1.7 - Math.sqrt(d2), 0, b.r * 1.7);
    if (inf > wInf) wInf = inf;
  }

  // raised biome patches (flower beds & dirt mounds sit proud of the grass);
  // suppressed near water banks and the beach so shores stay low
  if (r < cr * 0.82 && wInf < 0.6) {
    const bf = biomeField(x, z);
    const room = 1 - wInf; // fade out toward water
    // flower beds rise as clear little mounds; dirt & moss more gently
    if (bf > 0.74) h += (0.18 + band(bf, 0.74, 0.86) * 0.42) * room; // flower mound
    else if (bf > 0.58) h += band(bf, 0.58, 0.66) * 0.18 * room; // moss rise
    else if (bf < 0.28) h += band(0.28 - bf, 0, 0.1) * 0.3 * room; // dirt mound
  }

  // coastline dropoff into the sea
  const edge = (r - cr) / 6;
  if (edge > 0) h -= edge * edge * 7 + edge * 1.5;

  return h;
}

/** Which ground type paints a point (renderer + creature "at the beach"). */
export function groundBiome(x: number, z: number): Biome {
  const r = Math.hypot(x, z);
  const cr = coastRadius(Math.atan2(z, x));
  const wInf = waterInfluence(x, z);
  if (wInf > 0.35) return BIOME.MUD; // muddy banks around water
  if (r > cr * 0.85) return BIOME.SAND; // beach
  const bf = biomeField(x, z);
  if (bf > 0.74) return BIOME.FLOWER;
  if (bf > 0.58) return BIOME.MOSS;
  if (bf < 0.28) return BIOME.DIRT;
  return BIOME.GRASS;
}

/** Depth of standing water at a point (>0 = underwater). */
export function waterDepthAt(x: number, z: number): number {
  return WATER_LEVEL - terrainHeight(x, z);
}

export function pondSpotList(): readonly WaterSpot[] {
  return pondSpots;
}

/** Nearest pond within maxDist, or null (for creature water-play targeting). */
export function nearestPond(x: number, z: number, maxDist: number): WaterSpot | null {
  let best: WaterSpot | null = null;
  let bestD = maxDist;
  for (const s of pondSpots) {
    const d = Math.hypot(s.x - x, s.z - z);
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  return best;
}

/** Roam clamp: keep a point comfortably inside the actual coastline. */
export function landLimit(angle: number): number {
  return coastRadius(angle) * 0.8;
}
